import { useState } from 'react';

export default function CoCreationSection() {
  const [selectedPlan, setSelectedPlan] = useState<string>('poc-trial');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    plan: 'Plan B: PoC Trial（550,000円〜）',
    inquiry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const plans = [
    {
      id: 'dx-commit',
      badge: 'Standard',
      badgeColor: 'bg-gray-600',
      name: 'Plan A: DX Commit',
      target: '全社導入を見据えてじっくり取り組みたい企業',
      initialCost: '0円',
      initialLabel: '実証実験費用（初期）',
      initialNote: '全額免除',
      monthlyCost: '110,000円',
      monthlyLabel: 'システム利用料（月額）',
      contract: '2年契約',
      features: [
        '1日撮影（8時間・複数工程可）',
        'カット編集（AI最適化）',
        'AI解析レポート（視線・空間リスク）',
        'VR配信システム（JG+ Light）',
        '専任サポート担当'
      ],
      highlight: false,
      glowColor: ''
    },
    {
      id: 'poc-trial',
      badge: 'Recommended / 人気No.1',
      badgeColor: 'bg-gradient-to-r from-orange-500 to-red-600',
      name: 'Plan B: PoC Trial',
      target: 'まずは低予算で、1工程だけ試したい企業',
      initialCost: '¥550,000',
      initialLabel: 'PoC実証実験費用（初期）',
      initialNote: '',
      initialSubNote: '※撮影・カット編集・AI解析レポート作成費を含みます。',
      originalPrice: '¥1,155,000',
      discount: '50% OFF（パートナー優待）',
      monthlyCost: '¥110,000',
      monthlyLabel: 'システム利用料（月額）',
      monthlySubNote: '※JG+ Lightプラン（最大20台接続）1年契約',
      equipmentLabel: '必要機材（VRゴーグル）',
      equipmentCost: '別途お見積もり',
      equipmentNote: '※お手持ちのMeta Quest等も利用可能です。',
      contract: '1年契約',
      features: [
        '半日撮影（4時間）/ カット編集（AI最適化）',
        'AI解析レポート（視線・空間リスク）',
        'VR配信システム（JG+ Light）'
      ],
      highlight: true,
      glowColor: '#FF4500'
    },
    {
      id: 'one-shot',
      badge: 'Spot',
      badgeColor: 'bg-gray-600',
      name: 'Plan C: One-Shot',
      target: '月額契約不可。調査費のみで処理したい企業',
      initialCost: '1,650,000円',
      initialLabel: 'スポット費用',
      initialNote: '',
      monthlyCost: 'なし',
      monthlyLabel: 'システム利用料',
      contract: '単発納品',
      features: [
        '1日撮影（8時間）',
        'カット編集（AI最適化）',
        'AI解析レポート（視線・空間リスク）',
        '納品データ渡し（システム利用なし）'
      ],
      highlight: false,
      glowColor: ''
    }
  ];

  const deliverables = [
    {
      icon: 'ri-vr-line',
      title: 'Skills Archive',
      subtitle: '技能アーカイブ',
      description: '熟練工の視点と動きを、4K高画質の360度VRで永久保存。',
      note: '※現場の「生音」や「空気感」をそのまま残すRawデータ仕様（カット編集のみ）となります。'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'AI Analysis Report',
      subtitle: '解析レポート',
      description: '360度映像から、当事者視点の「技能」と「環境」を可視化します。',
      details: [
        '視線解析: 熟練工の「注視点」を可視化。',
        '空間・リスク解析: 作業中の「死角」に潜むリスクを検出。'
      ]
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Platform',
      subtitle: '配信基盤',
      description: '撮影したその日から、遠隔地の工場や研修施設へ一斉配信できる環境。'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, plan: e.target.value }));
  };

  const handlePreSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Validate textarea length
    const inquiry = data.get('inquiry') as string;
    if (inquiry && inquiry.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    // Basic spam prevention - check if filled too quickly (less than 3 seconds)
    const formFillTime = Date.now() - (window as any).formStartTime;
    if (formFillTime < 3000) {
      alert('送信が早すぎます。もう一度お試しください。');
      return;
    }

    // Email validation
    const email = data.get('email') as string;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('正しいメールアドレスを入力してください。');
      return;
    }

    // Update form data and show confirmation
    setFormData({
      company: data.get('company') as string,
      name: data.get('name') as string,
      email: email,
      plan: data.get('plan') as string,
      inquiry: inquiry
    });
    setShowConfirmation(true);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);

    try {
      const formDataToSend = new URLSearchParams({
        company: formData.company,
        name: formData.name,
        email: formData.email,
        plan: formData.plan,
        inquiry: formData.inquiry
      });

      const response = await fetch('https://readdy.ai/api/form/d4lp6mqt20jieso0puvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        alert('✅ 送信が完了しました。\n\n担当者より3営業日以内にご連絡いたします。\nご登録いただいたメールアドレスにも確認メールをお送りしております。');
        setShowConfirmation(false);
        setFormData({
          company: '',
          name: '',
          email: '',
          plan: 'Plan B: PoC Trial（550,000円〜）',
          inquiry: ''
        });
        // Reset form
        const form = document.getElementById('poc-entry-form') as HTMLFormElement;
        if (form) form.reset();
      } else {
        alert('❌ 送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('❌ 送信中にエラーが発生しました。しばらくしてから再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditForm = () => {
    setShowConfirmation(false);
  };

  return (
    <section className="relative w-full py-32 bg-gradient-to-b from-black via-[#0a0b0d] to-black">
      <div className="container mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
            <i className="ri-team-line text-orange-500 mr-3 text-xl"></i>
            <span className="text-orange-500 font-bold text-sm tracking-wider">CO-CREATION MODELS</span>
          </div>
          <h2 className="text-6xl font-black text-white mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            Join the Lab: Co-Creation Models
          </h2>
          <p className="text-3xl text-orange-400 font-bold mb-8" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            現場の「暗黙知」を資産化する。まずはPoCから始めませんか。
          </p>
          <div className="max-w-5xl mx-auto text-lg text-[#b0b8c1] leading-relaxed space-y-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            <p>
              熟練工の技をデジタル資産として残したいが、導入コストが壁になっている。そんな現場の声に応えます。
            </p>
            <p>
              <strong className="text-white">Multimodal AI Lab</strong>は、単なる映像制作ではなく、現場データを解析し新たな価値を共創するパートナーを探しています。
            </p>
            <p>
              実証実験（PoC）にご参画いただく企業様には、研究パートナー限定の特別プログラムとして、最新のAIデータ化技術を提供します。
            </p>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-gradient-to-br from-[#1a1d23] to-[#0a0b0d] rounded-3xl p-8 border-2 transition-all duration-500 ${
                plan.highlight 
                  ? 'border-orange-500 shadow-[0_0_60px_rgba(255,69,0,0.4)] lg:scale-105 lg:-translate-y-4' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              {/* Badge */}
              <div className={`inline-flex items-center px-4 py-2 ${plan.badgeColor} text-white text-xs font-bold rounded-full mb-6`}>
                {plan.badge}
              </div>

              {/* Plan Name */}
              <h3 className="text-3xl font-black text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                {plan.name}
              </h3>

              {/* Target */}
              <p className="text-sm text-[#b0b8c1] mb-8 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                {plan.target}
              </p>

              {/* Pricing */}
              <div className="bg-black/40 rounded-2xl p-6 mb-8 border border-gray-800">
                <div className="mb-6">
                  <p className="text-xs text-gray-400 mb-2">{plan.initialLabel}</p>
                  {plan.originalPrice && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-500 line-through">通常参考価格 {plan.originalPrice}</span>
                      </div>
                      <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                        {plan.discount}
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl font-black text-white">{plan.initialCost}</span>
                    <span className="text-sm text-gray-400">（税込）</span>
                  </div>
                  {plan.initialNote && (
                    <p className="text-xs text-orange-400 mt-2 font-bold">{plan.initialNote}</p>
                  )}
                  {plan.initialSubNote && (
                    <p className="text-xs text-gray-400 mt-2">{plan.initialSubNote}</p>
                  )}
                </div>
                <div className="pt-6 border-t border-gray-700 mb-6">
                  <p className="text-xs text-gray-400 mb-2">{plan.monthlyLabel}</p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-black text-white">{plan.monthlyCost}</span>
                    {plan.monthlyCost !== 'なし' && (
                      <span className="text-sm text-gray-400">（税込）/ 月</span>
                    )}
                  </div>
                  {plan.monthlySubNote && (
                    <p className="text-xs text-gray-400">{plan.monthlySubNote}</p>
                  )}
                </div>
                {plan.equipmentLabel && (
                  <div className="pt-6 border-t border-gray-700">
                    <p className="text-xs text-gray-400 mb-2">{plan.equipmentLabel}</p>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-xl font-bold text-white">{plan.equipmentCost}</span>
                      <span className="text-xs text-gray-400">（購入 または レンタル）</span>
                    </div>
                    {plan.equipmentNote && (
                      <p className="text-xs text-gray-400">{plan.equipmentNote}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Contract Period */}
              <div className="flex items-center gap-2 mb-6 text-sm text-gray-400">
                <i className="ri-calendar-line"></i>
                <span>{plan.contract}</span>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-orange-500 mt-1 flex-shrink-0"></i>
                    <span className="text-sm text-[#b0b8c1]" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              {plan.highlight && (
                <button
                  onClick={() => {
                    setSelectedPlan(plan.id);
                    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-orange-500/50"
                >
                  総額シミュレーション・相談をする
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Enterprise Plan */}
        <div className="relative bg-gradient-to-br from-[#001f3f] via-[#0a0b0d] to-black rounded-3xl p-12 border-2 border-cyan-500/30 mb-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl flex items-center justify-center">
                <i className="ri-building-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-4xl font-black text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  Enterprise / Custom Partnership
                </h3>
                <p className="text-lg text-cyan-400 font-bold" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  全社規模のDX、独自AIモデル開発、基幹システム連携など、大規模プロジェクトに対応します。
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-black/40 rounded-2xl p-8 border border-cyan-500/20">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  <i className="ri-cpu-line text-cyan-400"></i>
                  提供価値
                </h4>
                <p className="text-[#b0b8c1] leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  独自AIモデル開発、基幹システム連携、オンプレミス環境構築、教育カリキュラム化（テロップ・ナレーション編集等）など、大規模・多機能なプロジェクトに対応します。
                </p>
              </div>

              <div className="bg-black/40 rounded-2xl p-8 border border-cyan-500/20">
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  <i className="ri-price-tag-3-line text-cyan-400"></i>
                  価格
                </h4>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-4xl font-black text-white">Custom Quote</span>
                </div>
                <p className="text-sm text-gray-400" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  個別見積もり（プロジェクト規模・期間により変動）
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                setSelectedPlan('enterprise');
                document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              エンタープライズ案件について相談する
            </button>
          </div>
        </div>

        {/* Deliverables */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-black text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              The Deliverables
            </h3>
            <p className="text-xl text-[#b0b8c1]" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              納品されるAI資産
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {deliverables.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-[#1a1d23] to-[#0a0b0d] rounded-2xl p-8 border border-gray-700 hover:border-orange-500/50 transition-all duration-300 group">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${item.icon} text-4xl text-white`}></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {item.title}
                </h4>
                <p className="text-lg text-orange-400 font-bold mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {item.subtitle}
                </p>
                <p className="text-[#b0b8c1] leading-relaxed mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {item.description}
                </p>
                {item.note && (
                  <p className="text-xs text-gray-400 italic leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                    {item.note}
                  </p>
                )}
                {item.details && (
                  <ul className="space-y-2 mt-3">
                    {item.details.map((detail, detailIdx) => (
                      <li key={detailIdx} className="text-sm text-[#b0b8c1] flex items-start gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                        <span className="text-orange-500 mt-1">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div id="contact-form" className="max-w-3xl mx-auto bg-gradient-to-br from-[#1a1d23] to-[#0a0b0d] rounded-3xl p-12 border-2 border-orange-500/30">
          <div className="text-center mb-10">
            <h3 className="text-4xl font-black text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              Start Your PoC
            </h3>
            <p className="text-lg text-[#b0b8c1]" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              まずはお気軽にご相談ください。3営業日以内にご連絡いたします。
            </p>
          </div>

          {!showConfirmation ? (
            <form 
              id="poc-entry-form" 
              data-readdy-form 
              onSubmit={handlePreSubmit} 
              className="space-y-6"
              onFocus={() => {
                if (!(window as any).formStartTime) {
                  (window as any).formStartTime = Date.now();
                }
              }}
            >
              {/* Company Name */}
              <div>
                <label className="block text-sm font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="株式会社〇〇"
                />
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  担当者名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="山田 太郎"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors text-sm"
                  placeholder="example@company.co.jp"
                />
              </div>

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-bold text-white mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  ご興味のあるプラン <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {[
                    { value: 'poc-trial', label: 'Plan B: PoC Trial（550,000円〜）' },
                    { value: 'dx-commit', label: 'Plan A: DX Commit' },
                    { value: 'one-shot', label: 'Plan C: 単発解析' },
                    { value: 'enterprise', label: 'Enterprise / Custom Plan' },
                    { value: 'other', label: 'その他・相談したい' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="radio"
                        name="plan"
                        value={option.label}
                        defaultChecked={option.value === selectedPlan}
                        onChange={handleRadioChange}
                        className="w-5 h-5 text-orange-500 bg-black/40 border-gray-700 focus:ring-orange-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="text-sm text-[#b0b8c1] group-hover:text-white transition-colors" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Inquiry */}
              <div>
                <label className="block text-sm font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  お問い合わせ内容
                </label>
                <textarea
                  name="inquiry"
                  rows={6}
                  maxLength={500}
                  value={formData.inquiry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-black/40 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors resize-none text-sm"
                  placeholder="ご質問やご要望などをご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">※ 500文字以内でご入力ください</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-cyan-500/50"
              >
                入力内容を確認する
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Confirmation Screen */}
              <div className="bg-black/40 rounded-2xl p-8 border border-gray-700 space-y-6">
                <h4 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  <i className="ri-file-list-3-line text-orange-500"></i>
                  入力内容の確認
                </h4>

                <div className="space-y-4">
                  <div className="border-b border-gray-700 pb-4">
                    <p className="text-sm text-gray-400 mb-1">会社名</p>
                    <p className="text-white text-lg">{formData.company}</p>
                  </div>

                  <div className="border-b border-gray-700 pb-4">
                    <p className="text-sm text-gray-400 mb-1">担当者名</p>
                    <p className="text-white text-lg">{formData.name}</p>
                  </div>

                  <div className="border-b border-gray-700 pb-4">
                    <p className="text-sm text-gray-400 mb-1">メールアドレス</p>
                    <p className="text-white text-lg">{formData.email}</p>
                  </div>

                  <div className="border-b border-gray-700 pb-4">
                    <p className="text-sm text-gray-400 mb-1">ご興味のあるプラン</p>
                    <p className="text-white text-lg">{formData.plan}</p>
                  </div>

                  {formData.inquiry && (
                    <div className="border-b border-gray-700 pb-4">
                      <p className="text-sm text-gray-400 mb-1">お問い合わせ内容</p>
                      <p className="text-white text-lg whitespace-pre-wrap">{formData.inquiry}</p>
                    </div>
                  )}
                </div>

                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mt-6">
                  <p className="text-sm text-orange-400 flex items-start gap-2" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                    <i className="ri-information-line text-lg mt-0.5 flex-shrink-0"></i>
                    <span>
                      送信後、ご登録いただいたメールアドレスに確認メールをお送りします。<br />
                      担当者より3営業日以内にご連絡いたします。
                    </span>
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handleEditForm}
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-transparent border-2 border-gray-600 text-white font-bold text-lg rounded-xl hover:bg-gray-800 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  修正する
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold text-lg rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-orange-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      この内容で送信する
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
